// src/pages/kpis/KpiTecnicosPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";

import PeriodFilter from "../../components/Kpis/PeriodFilter";

import { db } from "../../config/firebase";
import { collection, getDocs, limit, query, Timestamp, where } from "firebase/firestore";

function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "object" && typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  return null;
}


function hoursBetween(a, b) {
  if (!a || !b) return null;
  const da = a instanceof Date ? a : a.toDate ? a.toDate() : new Date(a);
  const dbb = b instanceof Date ? b : b.toDate ? b.toDate() : new Date(b);
  const ms = dbb.getTime() - da.getTime();
  if (Number.isNaN(ms)) return null;
  return ms / 36e5;
}

function fmt(n, casas = 1) {
  if (n == null || Number.isNaN(n)) return "-";
  return Number(n).toFixed(casas);
}

export default function KpiTecnicosPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [periodo, setPeriodo] = useState({ preset: "30D", start: null, end: null });
  const [tecnicoId, setTecnicoId] = useState("__TODOS__");

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [tecnicos, setTecnicos] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [totais, setTotais] = useState({ atendimentos: 0, horas: 0 });

  const canLoad = useMemo(() => !!usuario?.grupoId, [usuario]);

  // Lista de técnicos (para o seletor)
  useEffect(() => {
    async function carregarTecnicos() {
      if (!usuario?.grupoId) return;
      try {
        const ref = collection(db, "usuarios");
        const q = query(ref, where("grupoId", "==", usuario.grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => {
            // nível pode ser numérico; se não existir, deixa fora do seletor
            const n = Number(u.nivel);
            return !Number.isNaN(n) ? n >= 3 : false;
          })
          .map((u) => ({
            id: u.id,
            nome: u.nome || u.displayName || u.email || u.id,
            nivel: u.nivel,
          }))
          .sort((a, b) => String(a.nome).localeCompare(String(b.nome)));

        setTecnicos(lista);
      } catch (e) {
        console.error(e);
        setTecnicos([]);
      }
    }
    carregarTecnicos();
  }, [usuario?.grupoId]);

  async function carregar() {
    if (!canLoad) return;
    if (!periodo?.start || !periodo?.end) return;

    setCarregando(true);
    setErro("");

    try {
      const ref = collection(db, "atendimentos");
      const q = query(ref, where("grupoId", "==", usuario.grupoId), limit(5000));

      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.dataFim);

      const startMs = periodo.start.getTime();
      const endMs = periodo.end.getTime();
      const lista = all
        .filter((a) => {
          const di = tsToDate(a.dataInicio);
          if (!di) return false;
          const ms = di.getTime();
          return ms >= startMs && ms <= endMs;
        })
        .sort((a, b) => (tsToDate(b.dataInicio)?.getTime() || 0) - (tsToDate(a.dataInicio)?.getTime() || 0));

      const map = new Map();
      let totalAt = 0;
      let totalHoras = 0;

      for (const a of lista) {
        const tid = a.tecnicoId || "(Sem técnico)";
        const tnome = a.tecnicoNome || tid;
        const h = hoursBetween(a.dataInicio, a.dataFim) || 0;

        totalAt += 1;
        totalHoras += h;

        const cur = map.get(tid) || { tecnicoId: tid, tecnicoNome: tnome, atendimentos: 0, horas: 0 };
        cur.atendimentos += 1;
        cur.horas += h;
        cur.tecnicoNome = tnome;
        map.set(tid, cur);
      }

      let rows = Array.from(map.values())
        .map((r) => ({
          ...r,
          percAtend: totalAt ? (r.atendimentos / totalAt) * 100 : 0,
          percHoras: totalHoras ? (r.horas / totalHoras) * 100 : 0,
        }))
        .sort((a, b) => b.atendimentos - a.atendimentos);

      if (tecnicoId && tecnicoId !== "__TODOS__") {
        rows = rows.filter((r) => r.tecnicoId === tecnicoId);
      }

      setTotais({ atendimentos: totalAt, horas: totalHoras });
      setLinhas(rows);
    } catch (e) {
      console.error(e);
      setErro("Erro ao carregar indicadores de técnicos. Verifique índices/consultas do Firestore.");
      setLinhas([]);
      setTotais({ atendimentos: 0, horas: 0 });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (periodo?.start && periodo?.end) {
      carregar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.grupoId, periodo?.start, periodo?.end, tecnicoId]);

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div className="pc-card" style={{ width: "100%" }}>
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>% Técnicos</h3>
            <p>Distribuição por técnico (atendimentos e horas) no período.</p>
          </div>
        </header>

        <PeriodFilter value={periodo} onApply={(v) => setPeriodo(v)} />

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginBottom: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, opacity: 0.85 }}>Técnico</span>
            <select
              className="kpi-period-select"
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
              style={{ minWidth: 240 }}
            >
              <option value="__TODOS__">Todos</option>
              {tecnicos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        {erro && (
          <div className="pc-alert pc-alert-error" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 12 }}>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Atendimentos finalizados (total)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{totais.atendimentos}</div>
          </div>
          <div className="pc-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Horas totais (total)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(totais.horas, 1)}</div>
          </div>
        </div>

        <div className="pc-card" style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {tecnicoId === "__TODOS__" ? "Ranking" : "Resumo"}
          </div>
          {carregando ? (
            <div>Carregando...</div>
          ) : linhas.length ? (
            <div style={{ overflowX: "auto" }}>
              <table className="pc-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Técnico</th>
                    <th style={{ textAlign: "right" }}>Atendimentos</th>
                    <th style={{ textAlign: "right" }}>% Atend.</th>
                    <th style={{ textAlign: "right" }}>Horas</th>
                    <th style={{ textAlign: "right" }}>% Horas</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((r) => (
                    <tr key={r.tecnicoId}>
                      <td>{r.tecnicoNome}</td>
                      <td style={{ textAlign: "right" }}>{r.atendimentos}</td>
                      <td style={{ textAlign: "right" }}>{fmt(r.percAtend, 1)}%</td>
                      <td style={{ textAlign: "right" }}>{fmt(r.horas, 1)}</td>
                      <td style={{ textAlign: "right" }}>{fmt(r.percHoras, 1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ opacity: 0.8 }}>Sem dados no período.</div>
          )}
        </div>
      </div>
    </section>
  );

  return isMobile ? (
    <LayoutMobile
      tema={tema}
      alterarTema={alterarTema}
      titulo="KPIs — % Técnicos"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
    >
      {conteudo}
    </LayoutMobile>
  ) : (
    <LayoutDesktop tema={tema} titulo="KPIs" subtitulo="% Técnicos">
      {conteudo}
    </LayoutDesktop>
  );
}